import React from 'react';

import { useForm } from 'react-hook-form';
import { InputAdornment, LbInput } from '@infoods/lifebase-ui';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

import classes from './IngredientSearchTabPanel.module.scss';
import {
  ScrollBox,
  WidgetEmptyPlaceholder,
} from '../../../../../../shared/components/ui-components';
import { useAutoSubmitForm } from '../../../../../../shared/hooks/useAutoSubmitForm';
import { useInfiniteScroll } from '../../../../../../shared/hooks/useInfiniteScroll';
import { FoodNutrientsCard } from '../../../../../components/FoodNutrientsCard/FoodNutrientsCard';
import { DatabaseFoodItem } from '../../../../../interfaces/DatabaseFoodItem';
import { FoodListItem } from '../../../../../interfaces/FoodListItem';
import { IngredientSearchTab } from '../constants/ingredientSearchTabs';
import { useIngredientSearch } from '../hooks/useIngredientSearch';
import { validateWhitespace } from '../../../../../../shared/utils/reactHookFormUtils';

type IngredientSearchTabPanelProps = {
  tab: IngredientSearchTab;
  onFoodSelect: (food: FoodListItem | DatabaseFoodItem) => void;
  onLoadingChange: (isLoading: boolean) => void;
};

/** tabs + search field + this = the modal's former 600px content area */
const FOOD_LIST_HEIGHT = 460;

type IngredientSearchFormValues = {
  searchTerm: string;
};

/** `LbTabPanel` mounts only the active panel, so a tab change remounts this - a fresh field and search */
const IngredientSearchTabPanel = ({
  tab,
  onFoodSelect,
  onLoadingChange,
}: IngredientSearchTabPanelProps) => {
  const { control, watch, handleSubmit } = useForm<IngredientSearchFormValues>({
    defaultValues: { searchTerm: '' },
  });

  const { foods, isSearch, hasMore, listKey, searchFoods, loadMoreFoods } =
    useIngredientSearch(tab, onLoadingChange);

  const { sentinelRef, rootRef } = useInfiniteScroll({
    hasMore,
    onLoadMore: loadMoreFoods,
  });

  useAutoSubmitForm(
    handleSubmit(({ searchTerm }) => searchFoods(searchTerm)),
    watch,
    ['searchTerm'],
  );

  return (
    <>
      <LbInput
        control={control}
        name="searchTerm"
        variant="outlined"
        size="medium"
        className={classes.searchInput}
        placeholder="Enter the ingredient name"
        dataTestId="ingredient-search-input"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <SearchRoundedIcon fontSize="large" color="tertiary" />
            </InputAdornment>
          ),
        }}
        rules={{ validate: validateWhitespace }}
      />
      {/* fixed height: one modal height across tabs; its scroll element is the sentinel's observer root.
          `key`: a new search keeps the old scroll offset otherwise - the user lands mid-list and the
          sentinel may already be in view; a new key remounts the box at the top. Load-more keeps the key. */}
      <ScrollBox
        key={listKey}
        maxHeight={FOOD_LIST_HEIGHT}
        variant="outBox"
        isFixedHeight
        scrollRef={rootRef}
      >
        <div className={classes.foodCardList}>
          {foods.map((food) => (
            <FoodNutrientsCard
              // own foods are unique by id, Nutritionix items (id 0) by bean id
              key={`${food.id}-${food.my_net_diary_bean_id}`}
              food={food}
              actionIcon={<AddCircleRoundedIcon color="success" fontSize="large" />}
              onClick={() => onFoodSelect(food)}
            />
          ))}
        </div>
        {hasMore && <div ref={sentinelRef} className={classes.loadMoreSentinel} />}
        {!foods.length && (
          <div className={classes.emptyList}>
            <WidgetEmptyPlaceholder
              icon={<SearchRoundedIcon fontSize="large" color="secondary" />}
              title={
                isSearch
                  ? "Your search didn't return any results"
                  : 'Input a search item name'
              }
              text={
                isSearch
                  ? 'Try a different search term'
                  : 'Start typing to find the item you need'
              }
            />
          </div>
        )}
      </ScrollBox>
    </>
  );
};

export { IngredientSearchTabPanel };
